// ENG-5901 Track D (ADR-0018 Phase 1): pure content model for the
// `.env.integrations` file, shared by its two writers.
//
// Why this exists
// ---------------
// `.env.integrations` historically had ONE writer (`writeIntegrations`),
// which rebuilt the whole file from the integration list each tick —
// full-overwrite semantics were how stale keys of disconnected
// integrations got pruned. Track D adds a SECOND writer: the channel
// credential path now stores raw channel tokens here (templated as
// `${VAR}` in `.mcp.json`). Two writers with full-overwrite semantics
// clobber each other's keys — the channel tick would wipe integration
// tokens and vice versa, killing whichever MCP loses the race.
//
// The fix is explicit key ownership, expressed as two merge modes over
// the same parsed model:
//
//   - 'upsert' (channel writer): overwrite ONLY the given keys,
//     preserve every other existing line.
//   - 'replace-preserving' (writeIntegrations): rebuild from the given
//     keys (so disconnected-integration pruning still works), carrying
//     over ONLY the channel-owned keys from the existing file.
//
// Pure functions, no fs — the claudecode adapter owns the read/write
// (agent dir + project mirror, both SECRET_FILE_MODE 0600).

/**
 * Wrap a value in single quotes and escape any embedded single quotes
 * using the bash idiom `'\''`. Safe for `source`-d shell files: bash
 * never interprets metacharacters inside single-quoted strings, so a
 * value like `$(rm -rf /)` becomes a literal string instead of being
 * executed.
 *
 * Lives here (not in the claudecode adapter) since ENG-5901 Track D so
 * the env-file content model has no import cycle with the adapter; the
 * adapter re-exports it for back-compat.
 */
export function shellQuote(value: string): string {
  return `'${value.replace(/'/g, `'\\''`)}'`;
}

/**
 * Channel-owned secret keys. The channel credential writer upserts
 * these; `writeIntegrations`' rebuild carries them over. Extend this
 * list when a new channel gains a secret env var — forgetting to do so
 * means the next integration tick wipes the new channel's token.
 */
export const CHANNEL_SECRET_ENV_KEYS: readonly string[] = [
  'SLACK_BOT_TOKEN',
  'SLACK_APP_TOKEN',
  'TELEGRAM_BOT_TOKEN',
  'MSTEAMS_CLIENT_SECRET',
];

/**
 * Secrets hoisted out of url-MCP server entries by `writeMcpServer`
 * (Composio `x-api-key`, Pipedream client secret). Like channel keys,
 * they are upserted outside `writeIntegrations`' rebuild and must be
 * carried over by 'replace-preserving' or the next integration tick
 * wipes them and the templated header/env substitutes to nothing.
 */
export const MCP_SERVER_SECRET_ENV_KEYS: readonly string[] = [
  'COMPOSIO_API_KEY',
  'PIPEDREAM_CLIENT_SECRET',
];

/** Default carry-over set for 'replace-preserving' merges. */
export const PRESERVED_ENV_KEYS: readonly string[] = [
  ...CHANNEL_SECRET_ENV_KEYS,
  ...MCP_SERVER_SECRET_ENV_KEYS,
];

const HEADER = '# Augmented integrations — auto-generated, do not edit';

/**
 * Parse `.env.integrations` content into an ordered map of
 * key → rendered value text (still shellQuoted exactly as on disk —
 * carrying lines over must not re-quote them). Comments/blank lines are
 * dropped; the canonical header is re-added on render.
 */
export function parseEnvFileEntries(content: string): Map<string, string> {
  const out = new Map<string, string>();
  for (const line of content.split('\n')) {
    if (!line || line.startsWith('#') || !line.includes('=')) continue;
    const eqIdx = line.indexOf('=');
    out.set(line.slice(0, eqIdx), line.slice(eqIdx + 1));
  }
  return out;
}

/** Render the canonical file: header + one KEY=<rendered> line each. */
export function renderEnvIntegrations(entries: Map<string, string>): string {
  const lines = [HEADER];
  for (const [key, rendered] of entries) lines.push(`${key}=${rendered}`);
  return lines.join('\n') + '\n';
}

export interface MergeEnvIntegrationsArgs {
  /**
   * 'upsert': overwrite only `updates` keys, keep everything else
   *   (channel credential writer).
   * 'replace-preserving': rebuild from `updates`, carrying over only
   *   `preserveKeys` from the existing content (writeIntegrations —
   *   keeps its stale-key pruning while sparing channel tokens).
   */
  mode: 'upsert' | 'replace-preserving';
  /**
   * RAW (unquoted) values — shellQuoted on render. A `null` value is an
   * explicit DELETE (CodeRabbit #1745): removal paths use it to evict a
   * writer-owned secret on disconnect; without it, preserved keys would
   * be carried forward indefinitely. Deletion wins over `preserveKeys`.
   */
  updates: Record<string, string | null>;
  /** Keys carried over verbatim in 'replace-preserving' mode.
   *  Defaults to {@link PRESERVED_ENV_KEYS} (channel + hoisted url-MCP
   *  secrets). */
  preserveKeys?: readonly string[];
}

/**
 * Merge new entries into existing `.env.integrations` content and
 * return the full new file body. `existing` is null when the file
 * doesn't exist yet.
 */
export function mergeEnvIntegrationsContent(
  existing: string | null,
  args: MergeEnvIntegrationsArgs,
): string {
  const current = existing === null ? new Map<string, string>() : parseEnvFileEntries(existing);

  let next: Map<string, string>;
  if (args.mode === 'upsert') {
    next = new Map(current);
    for (const [key, raw] of Object.entries(args.updates)) {
      if (raw === null) next.delete(key);
      else next.set(key, shellQuote(raw));
    }
  } else {
    next = new Map<string, string>();
    for (const [key, raw] of Object.entries(args.updates)) {
      if (raw !== null) next.set(key, shellQuote(raw));
    }
    const preserve = args.preserveKeys ?? PRESERVED_ENV_KEYS;
    for (const key of preserve) {
      // An explicitly-addressed key (set OR null-deleted) is never
      // resurrected by preserve; otherwise carry the existing rendered
      // value forward.
      if (key in args.updates) continue;
      if (!next.has(key) && current.has(key)) {
        next.set(key, current.get(key)!);
      }
    }
  }
  return renderEnvIntegrations(next);
}
